import { create } from 'zustand';
import { loadCumulativeLabResult, loadLabResult } from '../api/lab-result.queries.ts';
import type { CumulativeGroup, CumulativeLabResultResponse, LabResultResponse } from '../generated/openapi/befunde-api';
import { type LoadingState, newErrorState, newInitialLoadingState, newLoadedState, newLoadingState } from './LoadingState.ts';

export type ValuesFilter = 'ALL' | 'PATHOLOGICAL' | 'NONE_PATHOLOGICAL';
export type TabType = 'VALUE' | 'ATTACHMENT';

export type TabState = {
  type: TabType;
  tab: ValuesFilter | number;
};

export interface LabResultViewState {
  selectedId: string | undefined;
  labResultState: LoadingState<LabResultResponse>;
  cumulative: boolean;
  currentOnly: boolean;
  cumulativeLabResultState: LoadingState<CumulativeLabResultResponse>;
  selectedCumulativeGroups: CumulativeGroup[];
  tabState: TabState;
  tokens: string[];
}

export interface LabResultViewActions {
  setSelectedId: (selectedId: string | undefined) => void;
  loadLabResult: (force: boolean) => Promise<void>;
  switchCumulative: () => void;
  switchCurrentOnly: () => void;
  loadCumulativeLabResult: (force: boolean) => Promise<void>;
  setTab(type: TabType, tab: ValuesFilter | number): void;
  valuesFilter: () => ValuesFilter;
  setTokens: (tokens: string[]) => void;
}

const emptyState: LabResultViewState = {
  selectedId: undefined,
  labResultState: newInitialLoadingState<LabResultResponse>(),
  cumulative: false,
  currentOnly: false,
  cumulativeLabResultState: newInitialLoadingState<CumulativeLabResultResponse>(),
  selectedCumulativeGroups: [],
  tabState: { type: 'VALUE', tab: 'ALL' },
  tokens: [],
};

export const useLabResultViewStore = create<LabResultViewState & LabResultViewActions>((set, get) => ({
  ...emptyState,

  setSelectedId: selectedId => {
    if (get().selectedId !== selectedId) {
      set({
        ...emptyState,
        selectedId,
      });
    }
  },
  loadLabResult: async (force: boolean) => {
    if (get().labResultState.initial || force) {
      set({ labResultState: newLoadingState<LabResultResponse>() });
      try {
        const res = await loadLabResult({ orderResultId: get().selectedId || '', markAsRead: true });
        set({ labResultState: newLoadedState<LabResultResponse>(res?.data) });
      } catch (e) {
        set({ labResultState: newErrorState<LabResultResponse>(e) });
      }
    }
  },

  switchCumulative: () => set(state => ({ cumulative: !state.cumulative })),
  switchCurrentOnly: () =>
    set(state => ({
      currentOnly: !state.currentOnly,
      selectedCumulativeGroups: selectCumulativeGroups(state.cumulativeLabResultState, !state.currentOnly),
    })),
  loadCumulativeLabResult: async (force: boolean) => {
    if (get().cumulativeLabResultState.initial || force) {
      set({
        cumulativeLabResultState: newLoadingState<CumulativeLabResultResponse>(),
        selectedCumulativeGroups: [],
      });
      try {
        const res = await loadCumulativeLabResult({ orderResultId: get().selectedId || '' });
        const loadingState = newLoadedState<CumulativeLabResultResponse>(res?.data);
        set(state => ({
          cumulativeLabResultState: loadingState,
          selectedCumulativeGroups: selectCumulativeGroups(loadingState, state.currentOnly),
        }));
      } catch (e) {
        set({ cumulativeLabResultState: newErrorState<CumulativeLabResultResponse>(e), selectedCumulativeGroups: [] });
      }
    }
  },

  setTab: (type, tab) => set({ tabState: { type, tab } }),
  valuesFilter: () => (get().tabState.type === 'VALUE' ? (get().tabState.tab as ValuesFilter) : 'ALL'),
  setTokens: tokens => set({ tokens }),
}));

function selectCumulativeGroups(
  cumulativeLabResultState: LoadingState<CumulativeLabResultResponse>,
  currentOnly: boolean
): CumulativeGroup[] {
  const selectedIdx = cumulativeLabResultState.value?.selectedIdx === undefined ? -1 : cumulativeLabResultState.value?.selectedIdx;
  const groups = cumulativeLabResultState.value?.groups || [];

  if (groups.length > 0 && currentOnly) {
    return groups
      .map(group => {
        return {
          ...group,
          parameters: group.parameters.filter(p => selectedIdx === -1 || !!p.results[selectedIdx]),
        };
      })
      .filter(r => r.parameters.length > 0);
  }

  return groups;
}
